import React, { useState } from 'react';
import { Box, Toolbar, Collapse, Stack, useMediaQuery, Grid } from '@mui/material';
import { FilterConfig, GridProps } from '@sm-dashboard/types';
import { FiltersContent } from './FiltersContent';
import { ActionButtons } from './ActionsButtons';
import { MobilePrimaryFilterRow } from './MobilePrimaryFilterRow';
import { FilterInput } from './FilterInput';
import { isSearchFilter } from './filterFieldUtils';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

interface FiltersProps<T, U> {
  filters: GridProps<T, U>['filters'];
  exportConfig?: GridProps<T, U>['exportConfig'];
  filtersAttachedButton?: JSX.Element;
  promoteFirstRegularToPrimary?: boolean;
  hidePrimary?: boolean;
  /** Initial open state of the filters panel. Default: open only when filters are active in the URL. */
  defaultExpanded?: boolean;
  /**
   * No space under the filters (open or closed) — the parent adds its own fixed gap (GridData).
   * Default: the panel's own 16px margin when open, 5px under the buttons when closed.
   */
  flushBottom?: boolean;
}

export const Filters = <T, U>({
  filters,
  exportConfig,
  filtersAttachedButton,
  promoteFirstRegularToPrimary = true,
  hidePrimary = false,
  defaultExpanded,
  flushBottom = false
}: FiltersProps<T, U>) => {
  const { exportToExcel, disabled: exportDisabled, exportMenuItems, loading } = exportConfig || {};
  const [searchParams, setSearchParams] = useSearchParams();

  const hasActiveFilters = () => {
    const params = Array.from(searchParams.entries());
    const nonFilterKeys = new Set([
      '_timestamp',
      'page',
      'rowsPerPage',
      'sortField',
      'sortOrder',
      'dev',
      'web2applicationtoken',
    ]);

    const filterParams = params.filter(([key]) => !nonFilterKeys.has(key));

    return filterParams.length > 0;
  };

  const hasUserExportFilters = () =>
    Array.from(searchParams.keys()).some(
      key =>
        !new Set([
          '_timestamp',
          'page',
          'rowsPerPage',
          'sortField',
          'sortOrder',
          'sortDirection',
          'dev',
          'web2applicationtoken',
          'tzachOnly'
        ]).has(key)
    );

  const visibleExportConfig = exportConfig?.onlyWhenFiltered && !hasUserExportFilters() ? undefined : exportConfig;

  const [expanded, setExpanded] = useState(defaultExpanded ?? hasActiveFilters());
  const [openExcel, setOpenExcel] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  if (!filters) return null;

  filters = filters.filter(filter => filter.show === undefined || filter.show);

  const handleExcelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (exportDisabled || !exportToExcel) return;
    if (exportMenuItems) {
      setAnchorEl(event.currentTarget);
      setOpenExcel(true);
    } else {
      exportToExcel();
    }
  };

  const handleMenuItemClick = (onClick: () => void) => {
    return () => {
      setAnchorEl(null);
      setOpenExcel(false);
      onClick();
    };
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenExcel(false);
  };

  const [primaryFilters, regularFilters] = (() => {
    if (!filters) return [[], []];
    const primary: typeof filters = [];
    const regular: typeof filters = [];
    filters.forEach(filter => {
      if (filter.level === 'primary') {
        primary.push(filter);
      } else if (filter.level === 'regular' || !filter.level) {
        regular.push(filter);
      }
    });
    // If there are no primary filters, promote the first regular filter to primary
    if (primary.length === 0 && regular.length > 0 && promoteFirstRegularToPrimary) {
      primary.push(regular[0]);
      regular.splice(0, 1);
    }
    return [primary, regular];
  })();

  const searchFilter = primaryFilters.find(isSearchFilter);
  const otherPrimaryFilters = primaryFilters.filter(filter => !isSearchFilter(filter));
  const useMobileSearchRow = isSmallScreen && !hidePrimary && Boolean(searchFilter);

  const handleApplyFilters = (filter: FilterConfig<any>, value: any) => {
    const params = new URLSearchParams(searchParams);
    if (filter.type === 'multiSelect' || filter.type === 'sliderRange') {
      if (value?.length > 0) {
        params.set(String(filter.field), value?.join(','));
      } else {
        params.delete(String(filter.field));
      }
    } else if (filter.type === 'autocomplete' && value?.value) {
      const strVal = String(value?.value);
      const parts = strVal.split('-');
      if (parts.length === 2 && !strVal.includes(':')) {
        params.set(String(filter.field), parts[1]);
      } else {
        params.set(String(filter.field), strVal);
      }
    } else {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params.set(String(filter.field), value);
      } else {
        params.delete(String(filter.field));
      }
    }

    setSearchParams(params);

    if (
      useMobileSearchRow &&
      searchFilter &&
      String(filter.field) === String(searchFilter.field) &&
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ''
    ) {
      setExpanded(false);
    }
  };

  const renderFiltersContent = (filters: FilterConfig<any>[], isPrimary?: boolean) => {
    return <FiltersContent filters={filters} handleApplyFilters={handleApplyFilters} isPrimary={isPrimary} />;
  };

  const handleResetAllFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const actionButtonsProps = {
    hasActiveFilters: hasActiveFilters(),
    onReset: handleResetAllFilters,
    regularFiltersLength: regularFilters.length,
    expanded,
    onToggle: () => setExpanded(!expanded),
    exportConfig: visibleExportConfig,
    onExcelClick: handleExcelClick,
    openExcel,
    exportDisabled: exportDisabled || false,
    loading: loading || false,
    anchorEl,
    exportMenuItems,
    onMenuItemClick: handleMenuItemClick,
    onCloseMenu: handleCloseMenu
  };

  const combineAttachedWithMobileSearch = Boolean(useMobileSearchRow && searchFilter && filtersAttachedButton);

  const mobilePrimaryFilterRow = searchFilter ? (
    <MobilePrimaryFilterRow
      hasSearchFilter={false}
      hasActiveFilters={actionButtonsProps.hasActiveFilters}
      onReset={actionButtonsProps.onReset}
      regularFiltersLength={
        useMobileSearchRow
          ? regularFilters.length + 1 + otherPrimaryFilters.length
          : actionButtonsProps.regularFiltersLength
      }
      expanded={actionButtonsProps.expanded}
      onToggleFilters={actionButtonsProps.onToggle}
      exportProps={{
        exportConfig: actionButtonsProps.exportConfig,
        onExcelClick: actionButtonsProps.onExcelClick,
        openExcel: actionButtonsProps.openExcel,
        exportDisabled: actionButtonsProps.exportDisabled,
        loading: actionButtonsProps.loading,
        anchorEl: actionButtonsProps.anchorEl,
        exportMenuItems: actionButtonsProps.exportMenuItems,
        onMenuItemClick: actionButtonsProps.onMenuItemClick,
        onCloseMenu: actionButtonsProps.onCloseMenu
      }}
    />
  ) : null;

  const attachedButtonSx = combineAttachedWithMobileSearch
    ? {
        width: '100%',
        minWidth: 0,
        display: 'flex',
        justifyContent: 'center',
        '& .MuiStack-root': { width: '100%', gap: 0.5 },
        '& .MuiButton-root': {
          whiteSpace: 'nowrap',
          px: 0.75,
          py: 0.5,
          minWidth: 0,
          flex: 1,
          fontSize: '0.75rem',
          lineHeight: 1.25
        }
      }
    : {
        minWidth: isSmallScreen ? '120px' : 'auto',
        '& .MuiButton-root': {
          whiteSpace: 'nowrap',
          px: 2,
          width: { xs: '100%', md: 'auto' }
        }
      };

  return (
    <Stack gap={1} sx={{ width: '100%' }}>
      <Grid
        container
        sx={{
          gap: 1,
          width: '100%',
          alignItems: 'end',
          justifyContent: 'space-between'
        }}
      >
        {filtersAttachedButton && !combineAttachedWithMobileSearch && (
          <Grid item xs md={2.5}>
            <Box sx={attachedButtonSx}>{filtersAttachedButton}</Box>
          </Grid>
        )}

        {!isSmallScreen && <Grid item md={filtersAttachedButton ? 3.5 : 6}></Grid>}

        {useMobileSearchRow && searchFilter ? (
          <Grid item xs={12}>
            {combineAttachedWithMobileSearch ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  columnGap: 2.5,
                  rowGap: 0.75,
                  width: '100%',
                  minWidth: 0
                }}
              >
                <Box sx={attachedButtonSx}>{filtersAttachedButton}</Box>
                <Box
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  {mobilePrimaryFilterRow}
                </Box>
              </Box>
            ) : (
              mobilePrimaryFilterRow
            )}
          </Grid>
        ) : (
          <>
            {!hidePrimary && primaryFilters.length > 0 && (
              <Grid item xs={filtersAttachedButton ? 12 : true} md order={{ xs: filtersAttachedButton ? 1 : 0, md: 0 }}>
                {renderFiltersContent(primaryFilters, true)}
              </Grid>
            )}

            <Grid item xs={'auto'} sx={{ marginBottom: flushBottom ? 0 : '5px' }}>
              <ActionButtons {...actionButtonsProps} />
            </Grid>
          </>
        )}
      </Grid>

      {/* Additional filters */}
      <Collapse in={expanded}>
        <Toolbar
          disableGutters
          sx={{
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '18px',
            // flushBottom: the 5px moves above the panel and the parent owns the space under it.
            ...(flushBottom ? { mt: '5px' } : { mb: 2 }),
            py: 2,
            px: 2,
            bgcolor: 'grey.600',
            borderRadius: '15px',
          }}
        >
          {useMobileSearchRow && searchFilter ? (
            <Box sx={{ width: '100%', flexBasis: '100%' }}>
              <FilterInput filter={searchFilter} handleApplyFilters={handleApplyFilters} />
            </Box>
          ) : null}
          {useMobileSearchRow && otherPrimaryFilters.length > 0 ? (
            <Box sx={{ width: '100%', flexBasis: '100%' }}>
              {renderFiltersContent(otherPrimaryFilters, true)}
            </Box>
          ) : null}
          {renderFiltersContent(regularFilters, false)}
        </Toolbar>
      </Collapse>
    </Stack>
  );
};

export default Filters;
